import * as React from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import type { ProposalDocumentDto } from '@lib/dto/proposal-document.dto'
import { parseRenderGrant } from '@lib/validators/link-token.validator'
import { proposalService } from '@/services/ProposalService'
import { proposalKeys } from '@/hooks/queryKeys'
import { ProposalDocument } from '@/proposal-document/ProposalDocument'
import { useChoosing } from '@/proposal-document/choosing'

/** How long to wait for photos before printing what has loaded. */
const SETTLE_LIMIT_MS = 25_000

/**
 * A sent proposal laid out for Cloudflare's browser to print to PDF.
 *
 * Only the API's PDF printer opens this, on a short-lived grant rather than
 * a client's link, so it records nothing: no view and no activity. It is the
 * client's page without the page around it: no top bar, no Present, nothing
 * that tracks.
 *
 * `?link=` is the client's own link, when the PDF is their copy; the printed
 * QR codes open it. Without one, as for the staff copy, there are no codes.
 *
 * The printer cannot tell when a page that fetches its content is finished,
 * so this says: `data-proposal-ready` appears once the document, its fonts
 * and every photo have loaded (shrunk to print size) or failed. Nothing shows for a grant that does
 * not open, or is not shaped like one; the printer times out waiting, and the API reports a failed
 * PDF.
 */
export function PrintProposalPage() {
  const params = useParams<{ grant: string }>()
  const grant = parseRenderGrant(params.grant)
  const [search] = useSearchParams()
  const link = search.get('link')

  const document = useQuery({
    queryKey: proposalKeys.print(grant ?? ''),
    queryFn: () => proposalService.getShared(grant ?? ''),
    enabled: grant !== null,
    retry: false,
  })

  if (grant === null || document.data === undefined) return null
  return (
    <PrintedProposal
      document={document.data}
      grant={grant}
      shareUrl={
        link === null ? null : `${window.location.origin}/p/${encodeURIComponent(link)}`
      }
    />
  )
}

function PrintedProposal({
  document,
  grant,
  shareUrl,
}: {
  document: ProposalDocumentDto
  grant: string
  shareUrl: string | null
}) {
  const choosing = useChoosing(document)
  const isSettled = usePageSettled()

  return (
    <div className="proposal-stage" data-proposal-ready={isSettled ? '' : undefined}>
      <ProposalDocument
        document={document}
        choosing={choosing}
        photoSource={{ kind: 'shared', token: grant, forPrint: true }}
        shareUrl={shareUrl}
      />
    </div>
  )
}

function imageSettled(image: HTMLImageElement): Promise<void> {
  return new Promise((resolve) => {
    image.addEventListener('load', () => { resolve() }, { once: true })
    image.addEventListener('error', () => { resolve() }, { once: true })
  })
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * True once the fonts and every image on the page have loaded or failed, and
 * every photo has been swapped for its print-sized copy.
 *
 * Checked until a pass finds nothing pending, because a photo can mount a
 * frame after the document does and each one loads twice. Bounded, so an
 * image that never settles prints as it is rather than stopping the PDF.
 */
export function usePageSettled(): boolean {
  const [isSettled, setSettled] = React.useState(false)

  React.useEffect(() => {
    let isCancelled = false
    async function settle(): Promise<void> {
      if ('fonts' in window.document) await window.document.fonts.ready
      const giveUpAt = Date.now() + SETTLE_LIMIT_MS
      while (Date.now() < giveUpAt) {
        await new Promise((resolve) => requestAnimationFrame(resolve))
        const loading = [...window.document.images].filter((image) => !image.complete)
        const shrinking = window.document.querySelectorAll('[data-print-pending]').length
        if (loading.length === 0 && shrinking === 0) break
        await Promise.race([Promise.all(loading.map(imageSettled)), pause(150)])
      }
      if (!isCancelled) setSettled(true)
    }
    void settle()
    return () => {
      isCancelled = true
    }
  }, [])

  return isSettled
}
